import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { AlarmClock, RefreshCw, Siren, TriangleAlert, Users } from 'lucide-react';
import { useSystem } from '@ohif/core';
import PageLayout from '../../components/layouts/PageLayout';
import Pagination from '../../components/common/Pagination';
import EscalationMonitorSlaCell from '../../components/escalation/EscalationMonitorSlaCell';
import { CriticalNotificationDetailModal } from '../../components/criticalResultsDashboard';
import { authService } from '../../auth/authService';
import { ICON_STROKE_WIDTH } from '../../constants';
import { getRoleMeta } from '../../constants/escalationContact.constants';
import facilityService from '../../services/facilityService';
import {
  getEscalationSummary,
  listEscalationInstances,
} from '../../services/escalationRuntimeService';
import type { CriticalNotificationDetailContext } from '../../types/criticalNotification.types';
import type { EscalationContactRole } from '../../types/escalationContact.types';
import type {
  EscalationDashboardSummary,
  EscalationInstance,
  EscalationMonitorDisplayStatus,
} from '../../types/escalationRuntime.types';
import type { Facility } from '../../types/facility.types';
import { canAccessPage, ROLES, hasRole } from '../../utils/permissions';
import { getDefaultRoute } from '../../utils/getDefaultRoute';
import './EscalationMonitor.css';
import './RisConfiguration.css';

const PAGE_SIZE = 10;

const ESCALATION_MONITOR_BREADCRUMBS = [
  { label: 'Home', path: '/' },
  { label: 'Dashboard', path: '/admin-dashboard' },
  { label: 'Analytics' },
  { label: 'Escalation Monitor' },
];

function formatShortDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return '0m';
  }
  const minutes = Math.round(totalSeconds / 60);
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remMin = minutes % 60;
    return remMin > 0 ? `${hours}h ${remMin}m` : `${hours}h`;
  }
  return `${minutes}m`;
}

function getDisplayStatus(instance: EscalationInstance): EscalationMonitorDisplayStatus {
  if (instance.status === 'Acknowledged') {
    return 'Acknowledged';
  }
  if (instance.status === 'Completed' || instance.status === 'Cancelled') {
    return 'Closed';
  }
  if (instance.status === 'Active') {
    if (instance.slaStatus === 'Overdue') {
      return 'Breached';
    }
    if (
      instance.currentLevelNumber > 1 ||
      instance.isReminderPhase ||
      instance.reminderCountAtLevel > 0
    ) {
      return 'Escalating';
    }
    return 'Pending';
  }
  return 'Pending';
}

function statusPillClass(status: EscalationMonitorDisplayStatus) {
  switch (status) {
    case 'Pending':
      return 'esc-monitor-status--pending';
    case 'Escalating':
      return 'esc-monitor-status--escalating';
    case 'Breached':
      return 'esc-monitor-status--breached';
    case 'Acknowledged':
      return 'esc-monitor-status--acknowledged';
    case 'Closed':
      return 'esc-monitor-status--closed';
    default:
      return '';
  }
}

function formatRecipient(instance: EscalationInstance) {
  const contact = instance.currentContact;
  if (!contact) {
    return '—';
  }
  const roleLabel = getRoleMeta(contact.role as EscalationContactRole).label;
  return `${contact.name} (${roleLabel})`;
}

function formatStudyLabel(instance: EscalationInstance) {
  const displayName = instance.study?.studyDisplayName?.trim();
  if (displayName) {
    return displayName;
  }

  const bodyPart =
    instance.study?.testName?.trim() || instance.study?.studyDescription?.trim() || '';
  const modality = instance.study?.modalityName?.trim() || '';
  if (modality && bodyPart) {
    const bodyUpper = bodyPart.toUpperCase();
    const modalityUpper = modality.toUpperCase();
    if (bodyUpper.startsWith(modalityUpper)) {
      return bodyPart;
    }
    return `${modality} ${bodyPart}`;
  }
  return bodyPart || modality || '—';
}

function formatAccession(instance: EscalationInstance) {
  return instance.study?.accessionNumber?.trim() || instance.study?.risVisitId?.trim() || '—';
}

export default function EscalationMonitor() {
  const user = authService.getUser();
  const { servicesManager } = useSystem();
  const { uiNotificationService } = servicesManager.services;
  const isSuperAdmin = hasRole(user, ROLES.SUPER_ADMIN);

  const defaultFacilityId = useMemo(() => {
    const parsed = Number(authService.getFacilityId() || user?.facility_id);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [user]);

  const [facilityId, setFacilityId] = useState<number | null>(defaultFacilityId);
  const [facilityOptions, setFacilityOptions] = useState<Facility[]>([]);

  const [instances, setInstances] = useState<EscalationInstance[]>([]);
  const [summary, setSummary] = useState<EscalationDashboardSummary | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [timelineOpen, setTimelineOpen] = useState(false);
  const [detailContext, setDetailContext] = useState<CriticalNotificationDetailContext | null>(
    null
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filterParams = useMemo(
    () => ({
      facilityId: facilityId ?? 0,
      limit: PAGE_SIZE,
      offset: (currentPage - 1) * PAGE_SIZE,
    }),
    [facilityId, currentPage]
  );

  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }
    facilityService.getAllFacilities(1).then(response => {
      if (response.success && response.data) {
        setFacilityOptions(response.data);
        if (!facilityId && response.data.length > 0) {
          setFacilityId(Number(response.data[0].id));
        }
      }
    });
  }, [isSuperAdmin, facilityId]);

  const loadSummary = useCallback(async () => {
    if (!facilityId) {
      return;
    }
    const result = await getEscalationSummary({ facilityId });
    if (result.success && result.data) {
      setSummary(result.data);
    }
  }, [facilityId]);

  const loadInstances = useCallback(async () => {
    if (!facilityId) {
      return;
    }
    setLoading(true);
    setError(null);
    const result = await listEscalationInstances(filterParams);
    if (!result.success) {
      setError(result.error || 'Failed to load escalations');
      setInstances([]);
      setTotalCount(0);
    } else {
      setInstances(result.data);
      setTotalCount(result.count);
    }
    setLoading(false);
  }, [facilityId, filterParams]);

  const refreshAll = useCallback(() => {
    loadSummary();
    loadInstances();
  }, [loadSummary, loadInstances]);

  useEffect(() => {
    loadSummary();
    loadInstances();
  }, [loadSummary, loadInstances]);

  useEffect(() => {
    const timer = setInterval(refreshAll, 30000);
    return () => clearInterval(timer);
  }, [refreshAll]);

  const openTimeline = (instance: EscalationInstance) => {
    if (!facilityId) {
      return;
    }
    setDetailContext({
      facilityId,
      escalationInstanceId: instance.id,
      criticalNotificationId: instance.criticalNotification?.id ?? null,
      accessionLabel: formatAccession(instance),
      studyLabel: formatStudyLabel(instance),
      patientName: instance.study?.patientName?.trim() || null,
      patientUhid: instance.study?.patientUhid?.trim() || null,
      orderingClinician: instance.study?.referredBy?.trim() || null,
      risVisitId:
        instance.study?.risVisitId?.trim() || instance.study?.accessionNumber?.trim() || null,
    });
    setTimelineOpen(true);
  };

  const closeTimeline = () => {
    setTimelineOpen(false);
    setDetailContext(null);
  };

  if (!canAccessPage(user, '/escalation-monitor')) {
    return (
      <Navigate
        to={getDefaultRoute(user)}
        replace
      />
    );
  }

  if (!defaultFacilityId && !isSuperAdmin) {
    return (
      <PageLayout
        title="Escalation Monitor"
        breadcrumbs={ESCALATION_MONITOR_BREADCRUMBS}
      >
        <p className="esc-monitor-error">Your account is not associated with a facility.</p>
      </PageLayout>
    );
  }

  if (!facilityId) {
    return (
      <PageLayout
        title="Escalation Monitor"
        breadcrumbs={ESCALATION_MONITOR_BREADCRUMBS}
      >
        <p className="esc-monitor-placeholder">
          {isSuperAdmin
            ? 'Select a facility to view the escalation monitor.'
            : 'Your account is not associated with a facility.'}
        </p>
      </PageLayout>
    );
  }

  const statusCounts = summary?.statusCounts;

  return (
    <PageLayout
      title="Escalation Monitor"
      hideTitleInHeader
      breadcrumbs={ESCALATION_MONITOR_BREADCRUMBS}
    >
      <div className="esc-monitor-page">
        <div className="esc-monitor-top-actions">
          {isSuperAdmin ? (
            <select
              className="esc-input esc-select esc-monitor-facility-select"
              value={facilityId ?? ''}
              onChange={e => {
                setFacilityId(Number(e.target.value));
                setCurrentPage(1);
                closeTimeline();
              }}
            >
              <option
                value=""
                disabled
              >
                Select facility
              </option>
              {facilityOptions.map(facility => (
                <option
                  key={facility.id}
                  value={facility.id}
                >
                  {facility.facility_name}
                </option>
              ))}
            </select>
          ) : null}
          <button
            type="button"
            className="esc-btn esc-btn--secondary esc-btn--inline"
            onClick={refreshAll}
            disabled={loading}
          >
            <RefreshCw
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
            />
            Refresh
          </button>
        </div>

        <div className="esc-monitor-summary">
          <article className="esc-monitor-card">
            <div className="esc-monitor-card-body">
              <p className="esc-monitor-card-label">Active alerts</p>
              <p className="esc-monitor-card-value">{summary?.active ?? 0}</p>
              <p className="esc-monitor-card-hint">awaiting acknowledgement</p>
            </div>
            <Siren
              className="esc-monitor-card-icon"
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
              aria-hidden
            />
          </article>
          <article className="esc-monitor-card">
            <div className="esc-monitor-card-body">
              <p className="esc-monitor-card-label">SLA breaches</p>
              <p className="esc-monitor-card-value">
                {summary?.slaBreaches ?? summary?.overdue ?? 0}
              </p>
              <p className="esc-monitor-card-hint">need attention</p>
            </div>
            <TriangleAlert
              className="esc-monitor-card-icon"
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
              aria-hidden
            />
          </article>
          <article className="esc-monitor-card">
            <div className="esc-monitor-card-body">
              <p className="esc-monitor-card-label">Avg ack TAT</p>
              <p className="esc-monitor-card-value">
                {formatShortDuration(summary?.avgAckTatSeconds ?? 0)}
              </p>
              <p className="esc-monitor-card-hint">acknowledged today</p>
            </div>
            <AlarmClock
              className="esc-monitor-card-icon"
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
              aria-hidden
            />
          </article>
          <article className="esc-monitor-card">
            <div className="esc-monitor-card-body">
              <p className="esc-monitor-card-label">Active contacts</p>
              <p className="esc-monitor-card-value">{summary?.activeContacts ?? 0}</p>
              <p className="esc-monitor-card-hint">across all roles</p>
            </div>
            <Users
              className="esc-monitor-card-icon"
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
              aria-hidden
            />
          </article>
        </div>

        <section className="esc-monitor-table-section">
          <header className="esc-monitor-table-header">
            <h2 className="esc-monitor-table-title">Live escalation monitor</h2>
            {statusCounts ? (
              <div
                className="esc-monitor-legend"
                aria-label="Status summary"
              >
                <span className="esc-monitor-legend-item esc-monitor-legend-item--pending">
                  {statusCounts.pending} pending
                </span>
                <span className="esc-monitor-legend-item esc-monitor-legend-item--escalating">
                  {statusCounts.escalating} escalating
                </span>
                <span className="esc-monitor-legend-item esc-monitor-legend-item--breached">
                  {statusCounts.breached} breached
                </span>
                <span className="esc-monitor-legend-item esc-monitor-legend-item--closed">
                  {statusCounts.closed} closed
                </span>
              </div>
            ) : null}
          </header>

          {error ? (
            <p
              className="esc-monitor-error"
              role="alert"
            >
              {error}
            </p>
          ) : null}

          <div className="esc-monitor-table-wrap">
            <table className="esc-monitor-table">
              <thead>
                <tr>
                  <th>Accession</th>
                  <th>Patient</th>
                  <th>Study</th>
                  <th>Band</th>
                  <th>Level</th>
                  <th>Current recipient</th>
                  <th>Status</th>
                  <th>SLA</th>
                  <th>Timeline</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="esc-monitor-table-empty"
                    >
                      Loading escalations…
                    </td>
                  </tr>
                ) : instances.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="esc-monitor-table-empty"
                    >
                      No escalations to display.
                    </td>
                  </tr>
                ) : (
                  instances.map(instance => {
                    const displayStatus = getDisplayStatus(instance);
                    return (
                      <tr key={instance.id}>
                        <td className="esc-monitor-cell-accession">{formatAccession(instance)}</td>
                        <td className="esc-monitor-cell-patient">
                          <span className="esc-monitor-patient-name">
                            {instance.study?.patientName?.trim() || '—'}
                          </span>
                          {instance.study?.patientUhid?.trim() ? (
                            <span className="esc-monitor-patient-uhid">
                              {instance.study.patientUhid.trim()}
                            </span>
                          ) : null}
                        </td>
                        <td>{formatStudyLabel(instance)}</td>
                        <td>
                          <span
                            className={`esc-criticality-badge esc-criticality-badge--${instance.criticalityBand === 'Major' ? 'major' : 'minor'}`}
                          >
                            {instance.criticalityBand}
                          </span>
                        </td>
                        <td>
                          <span className="esc-monitor-level-pill">
                            L{instance.currentLevelNumber}
                          </span>
                        </td>
                        <td className="esc-monitor-cell-recipient">{formatRecipient(instance)}</td>
                        <td>
                          <span className={`esc-monitor-status ${statusPillClass(displayStatus)}`}>
                            {displayStatus}
                          </span>
                        </td>
                        <td>
                          <EscalationMonitorSlaCell instance={instance} />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="esc-monitor-timeline-link"
                            onClick={() => openTimeline(instance)}
                          >
                            Timeline →
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {totalCount > PAGE_SIZE ? (
            <Pagination
              currentPage={currentPage}
              totalItems={totalCount}
              itemsPerPage={PAGE_SIZE}
              onPageChange={setCurrentPage}
            />
          ) : null}
        </section>
      </div>

      <CriticalNotificationDetailModal
        open={timelineOpen}
        context={detailContext}
        onClose={closeTimeline}
        onAcknowledged={() => {
          uiNotificationService?.show({
            title: 'Acknowledged',
            message: 'Escalation workflow has been stopped.',
            type: 'success',
            duration: 3000,
          });
          refreshAll();
        }}
      />
    </PageLayout>
  );
}
